import type { RouterContextType } from "@/api/permissions/permissions.api";
import BasicInformation from "@/components/features/ClientOnboarding/BasicInformation/BasicInformation";
import Brands from "@/components/features/ClientOnboarding/Brands/Brands";
import ClientSummary from "@/components/features/ClientOnboarding/ClientSummary/ClientSummary";
import LocationInfo from "@/components/features/ClientOnboarding/LocationInfo/LocationInfo";
import { tableSearchSchema } from "@/hooks/useTableUrlPersistence";
import { useClientOnboardingStore } from "@/store/clientOnboardingStore";
import { useCoverageStore } from "@/store/coverageStore/coverageStore";
import { authGuardWithPermissions } from "@/utils/authGuard";
import { handleApiSuccess } from "@/utils/responseHandler";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { stepConfig } from "../../_addNewClientLayout";

export const Route = createFileRoute("/clients/add-new-client/_addNewClientLayout/$stepId/")({
  component: ClientStepComponent,
  validateSearch: tableSearchSchema.parse,
  beforeLoad: async (args) => {
    await authGuardWithPermissions({
      context: args.context as RouterContextType,
      location: args.location as { pathname: string },
    });
  },
});

export type TAddClientStepId = "basic-info" | "brands" | "locations";

// A19 (MVP-hidden, restore pending product confirm): the Summary step was removed from
// the trimmed create wizard (46e5aec7) but the component is preserved per the MVP
// hide-don't-delete rule. Restoring it also requires re-adding a "summary" step to
// stepConfig in _addNewClientLayout.tsx.
const SHOW_CLIENT_SUMMARY_STEP = false;

function ClientStepComponent() {
  const { stepId } = Route.useParams() as { stepId: TAddClientStepId };
  const navigate = useNavigate();
  const { clientId, setCurrentStep, setClientId, resetOnboarding } = useClientOnboardingStore();
  const { resetCoverageStore } = useCoverageStore();

  const currentStepConfig = stepConfig.find((step) => step.stepKey === stepId);

  // Everything past the three creation steps is filled in after the client
  // exists, from its detail tabs — send stray/legacy URLs back to the start.
  if (!currentStepConfig) {
    navigate({ to: "/clients/add-new-client/$stepId", params: { stepId: "basic-info" } });
    return null;
  }

  if (!clientId && stepId !== "basic-info") {
    navigate({ to: "/clients/add-new-client/$stepId", params: { stepId: "basic-info" } });
    return null;
  }

  const finishToClientProfile = () => {
    if (!clientId) return;
    handleApiSuccess("The client has been created successfully", "Client created successfully");
    // Reset only after navigation settles — clearing clientId while still on a
    // wizard route would trip the guard above and bounce back to Basic Info.
    void navigate({ to: "/clients/$id", params: { id: clientId } }).then(() => {
      setClientId("");
      resetOnboarding();
      resetCoverageStore();
      localStorage.removeItem("client-onboarding-storage");
    });
  };

  // Advance to the next creation step, or finish to the client profile on the last one.
  const goToNextStep = () => {
    const index = stepConfig.findIndex((step) => step.stepKey === stepId);
    const nextStep = stepConfig[index + 1];
    if (nextStep) {
      setCurrentStep(index + 2);
      navigate({ to: `/clients/add-new-client/${nextStep.stepKey}` });
    } else {
      finishToClientProfile();
    }
  };

  switch (stepId) {
    case "basic-info":
      return <BasicInformation />;
    case "brands":
      return <Brands />;
    case "locations":
      return <LocationInfo skipStepAction={goToNextStep} />;
    default:
      return SHOW_CLIENT_SUMMARY_STEP ? <ClientSummary /> : null;
  }
}
